const FORM_START_EVENT = 'form_start';
const FORM_SUBMIT_EVENT = 'form_submit';
const FILE_DOWNLOAD_EVENT = 'file_download';

const TRACKED_DOCUMENT_EXTENSIONS = new Set([
	'pdf',
	'doc',
	'docx',
	'ppt',
	'pptx',
	'xls',
	'xlsx',
]);

const trackedForms = new WeakSet();

const ensureDataLayer = () => {
	if (typeof window === 'undefined') {
		return [];
	}

	window.dataLayer = window.dataLayer || [];

	return window.dataLayer;
};

const pushEvent = (eventName, params = {}) => {
	const dataLayer = ensureDataLayer();

	dataLayer.push({
		event: eventName,
		...params,
	});
};

const parseFormConfig = ($form, formInstance) => {
	if (formInstance?.config) {
		return formInstance.config;
	}

	const { fuiForm } = $form.dataset || {};

	if (fuiForm) {
		try {
			return JSON.parse(fuiForm);
		} catch (error) {
			console.warn('[Analytics] Unable to parse Formie config JSON', error);
		}
	}

	return null;
};

const getHiddenInputValue = ($form, selector) => {
	const input = $form.querySelector(selector);

	return input ? input.value : null;
};

const getFormTitle = ($form) => {
	const titleEl = $form.querySelector('.fui-title');

	if (titleEl && titleEl.textContent) {
		return titleEl.textContent.trim();
	}

	return null;
};

const getFormMeta = ($form, formInstance) => {
	const config = parseFormConfig($form, formInstance) || {};
	const settings = config.settings || {};

	const hiddenHandle =
		getHiddenInputValue($form, 'input[name="handle"]') ||
		getHiddenInputValue($form, 'input[name="formHandle"]') ||
		getHiddenInputValue($form, 'input[name="formieFormHandle"]');

	const meta = {
		formId:
			config.formId ||
			$form.getAttribute('data-form-id') ||
			getHiddenInputValue($form, 'input[name="formId"]') ||
			getHiddenInputValue($form, 'input[name="formieFormId"]') ||
			$form.id ||
			'unknown',
		formHandle:
			config.formHandle ||
			$form.getAttribute('data-form-handle') ||
			hiddenHandle ||
			'unknown',
		formName:
			$form.dataset.formName ||
			getFormTitle($form) ||
			settings.formName ||
			config.formHandle ||
			hiddenHandle ||
			'Unknown Form',
		settings,
	};

	return meta;
};

const trackFormStart = ($form, meta) => {
	if ($form.dataset.formStartTracked === 'true') {
		return;
	}

	$form.dataset.formStartTracked = 'true';

	pushEvent(FORM_START_EVENT, {
		'Form Name': meta.formName,
		'Form Handle': meta.formHandle,
		'Form Id': meta.formId,
	});
};

const resetFormSubmitState = ($form) => {
	delete $form.dataset.formSubmitTracked;
};

const trackFormSubmit = ($form, meta) => {
	if ($form.dataset.formSubmitTracked === 'true') {
		return;
	}

	$form.dataset.formSubmitTracked = 'true';

	pushEvent(FORM_SUBMIT_EVENT, {
		'Form Name': meta.formName,
		'Form Handle': meta.formHandle,
		'Form Id': meta.formId,
	});
};

const registerFormTracking = ($form, formInstance) => {
	if (!$form || trackedForms.has($form)) {
		return;
	}

	const meta = getFormMeta($form, formInstance);
	const config = parseFormConfig($form, formInstance) || {};
	const submitMethod = config.settings?.submitMethod || '';
	const isAjaxSubmit = submitMethod.toLowerCase() === 'ajax';

	trackedForms.add($form);

	const startListener = () => trackFormStart($form, meta);

	$form.addEventListener('focusin', startListener, { passive: true });
	$form.addEventListener('input', startListener, { passive: true });

	if (isAjaxSubmit) {
		$form.addEventListener('onAfterFormieSubmit', (event) => {
			// Multi-page forms emit this event between pages with nextPageId set.
			if (event?.detail?.nextPageId) {
				return;
			}

			trackFormSubmit($form, meta);
		});

		$form.addEventListener('onFormieSubmitError', () => {
			resetFormSubmitState($form);
		});
	} else {
		$form.addEventListener('submit', () => {
			trackFormSubmit($form, meta);
		});
	}
};

const registerExistingForms = () => {
	if (window.Formie?.forms?.length) {
		window.Formie.forms.forEach((formInstance) => {
			registerFormTracking(formInstance.$form, formInstance);
		});
	}

	document
		.querySelectorAll('form[data-fui-form]')
		.forEach(($form) => registerFormTracking($form));
};

const initFormTracking = () => {
	document.addEventListener('onFormieInit', (event) => {
		if (!event?.detail?.$form) {
			return;
		}

		registerFormTracking(event.detail.$form, event.detail.form);
	});

	document.addEventListener('onFormieLoaded', () => {
		registerExistingForms();
	});

	// Fallback for forms that might already be initialised before listeners are attached.
	if (document.readyState === 'complete' || document.readyState === 'interactive') {
		registerExistingForms();
	} else {
		document.addEventListener('DOMContentLoaded', registerExistingForms, { once: true });
	}
};

const extractFileInfoFromUrl = (link) => {
	try {
		const url = new URL(link.getAttribute('href'), window.location.origin);
		const pathname = url.pathname || '';
		const parts = pathname.split('/');
		const filename = parts.pop() || link.textContent?.trim() || 'Unknown File';

		return {
			fileName: decodeURIComponent(filename),
			extension: filename.includes('.') ? filename.split('.').pop().toLowerCase() : '',
		};
	} catch (error) {
		return {
			fileName: link.textContent?.trim() || 'Unknown File',
			extension: '',
		};
	}
};

const shouldTrackDownload = (link) => {
	if (link.hasAttribute('data-file-download')) {
		return true;
	}

	if (!link.hasAttribute('href')) {
		return false;
	}

	const { extension } = extractFileInfoFromUrl(link);

	return TRACKED_DOCUMENT_EXTENSIONS.has(extension);
};

const getDownloadMeta = (link) => {
	const dataName = link.getAttribute('data-file-download');

	if (dataName) {
		return {
			fileName: dataName,
			fileId: link.getAttribute('data-file-id') || 'unknown',
		};
	}

	const { fileName } = extractFileInfoFromUrl(link);

	return {
		fileName,
		fileId: 'unknown',
	};
};

const initDownloadTracking = () => {
	document.addEventListener(
		'click',
		(event) => {
			const link = event.target.closest('a');

			if (!link || !shouldTrackDownload(link)) {
				return;
			}

			const { fileName, fileId } = getDownloadMeta(link);

			pushEvent(FILE_DOWNLOAD_EVENT, {
				'File Name': fileName,
				Id: fileId,
			});
		},
		true,
	);
};

const initAnalytics = () => {
	ensureDataLayer();
	initFormTracking();
	initDownloadTracking();
};

initAnalytics();

export {};
